import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  Tab,
  Tabs,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  Add as AddIcon,
  Close as CloseIcon,
  FileCopy as FileCopyIcon,
  Star as StarIcon,
  StarOutline as StarOutlineIcon,
} from "@mui/icons-material";
import { authenticatedApi } from "../../utils/api";
import { apiService } from "../../config/api";

interface Template {
  id: string;
  template_name: string;
  subject_line: string;
  message_body: string;
  alert_types: string[];
  is_custom: boolean;
  is_default?: boolean;
  usage_count?: number;
  last_used_at?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TemplateEditor: React.FC<{ studyId: string; onClose?: () => void }> = ({
  studyId,
  onClose,
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [availableVariables, setAvailableVariables] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "success" });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAlertTypeFilter, setSelectedAlertTypeFilter] =
    useState<string>("all");
  const [formData, setFormData] = useState({
    templateName: "",
    subjectLine: "",
    messageBody: "",
    alertType: "",
  });

  const alertTypeOptions = [
    "No Sync (>48h data gap)",
    "No Wear (Full Day)",
    "No Wear (Full Day) (Streak)",
    "Partial Wear (Insufficient Wear Time)",
    "Off-Wrist Overnight (Likely Not Worn at Night) (Streak)",
    "Off-Wrist Overnight (Likely Not Worn at Night)",
    "Low Night Coverage (14d)",
    "No Wear (Classic: Zero Steps & High Sedentary)",
  ];
  useEffect(() => {
    fetchTemplates();
    fetchAvailableVariables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = await apiService.getAuthToken();
      if (!token) throw new Error("Authentication required");
      const response = await authenticatedApi.get<any>(
        `/api/templates/study/${studyId}`,
        token
      );
      if (response.success) {
        setTemplates(response.templates);
      }
    } catch (error) {
      // Silently handle error
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableVariables = async () => {
    try {
      const token = await apiService.getAuthToken();
      if (token) {
        const response = await authenticatedApi.get<any>(
          "/api/templates/variables",
          token
        );
        if (response.success) setAvailableVariables(response.variables);
      } else {
        // Fallback unauthenticated fetch
        const res = await fetch(
          `${
            process.env.REACT_APP_API_URL || "http://localhost:3002"
          }/api/templates/variables`
        );
        const data = await res.json();
        if (data.success) setAvailableVariables(data.variables);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  const handleOpenEditor = (template?: Template) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        templateName: template.template_name,
        subjectLine: template.subject_line,
        messageBody: template.message_body,
        alertType: template.alert_types?.[0] || "",
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        templateName: "",
        subjectLine: "",
        messageBody: "",
        alertType: "",
      });
    }
    setShowEditorModal(true);
  };

  const handleSaveTemplate = async () => {
    try {
      if (
        !formData.templateName ||
        !formData.subjectLine ||
        !formData.messageBody ||
        !formData.alertType
      ) {
        setSnackbar({
          open: true,
          message: "Please fill in all required fields including alert type",
          severity: "error",
        });
        return;
      }

      const payload = {
        templateName: formData.templateName,
        subjectLine: formData.subjectLine,
        messageBody: formData.messageBody,
        alertTypes: [formData.alertType],
        studyId,
      };

      let response;
      const token = await apiService.getAuthToken();
      if (!token) throw new Error("Authentication required");
      if (editingTemplate) {
        if (editingTemplate.is_custom === false) {
          setSnackbar({
            open: true,
            message: "Cannot edit default templates",
            severity: "error",
          });
          return;
        }
        response = await authenticatedApi.put<any>(
          `/api/templates/${editingTemplate.id}`,
          payload,
          token
        );
      } else {
        response = await authenticatedApi.post<any>(
          "/api/templates",
          payload,
          token
        );
      }

      if (response.success) {
        setSnackbar({
          open: true,
          message: "Template saved successfully",
          severity: "success",
        });
        setShowEditorModal(false);
        fetchTemplates();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to save template",
        severity: "error",
      });
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }

    try {
      const token = await apiService.getAuthToken();
      if (!token) throw new Error("Authentication required");
      const response = await authenticatedApi.delete<any>(
        `/api/templates/${templateId}`,
        token
      );
      if (response.success) {
        setSnackbar({
          open: true,
          message: "Template deleted successfully",
          severity: "success",
        });
        fetchTemplates();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete template",
        severity: "error",
      });
    }
  };

  const handleDuplicateTemplate = async (template: Template) => {
    try {
      const payload = {
        templateName: `${template.template_name} (Copy)`,
        subjectLine: template.subject_line,
        messageBody: template.message_body,
        alertTypes: template.alert_types,
        studyId,
      };

      const token = await apiService.getAuthToken();
      if (!token) throw new Error("Authentication required");
      const response = await authenticatedApi.post<any>(
        "/api/templates",
        payload,
        token
      );
      if (response.success) {
        setSnackbar({
          open: true,
          message: "Template duplicated successfully",
          severity: "success",
        });
        fetchTemplates();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to duplicate template",
        severity: "error",
      });
    }
  };

  const handleMakeDefault = async (templateId: string, alertType: string) => {
    try {
      const token = await apiService.getAuthToken();
      if (!token) throw new Error("Authentication required");
      const response = await authenticatedApi.post<any>(
        `/api/templates/${templateId}/set-default`,
        { studyId, alertType },
        token
      );
      if (response.success) {
        setSnackbar({
          open: true,
          message: `Template set as default for ${alertType}`,
          severity: "success",
        });
        fetchTemplates();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to set template as default",
        severity: "error",
      });
    }
  };

  const handlePreview = async (template: Template) => {
    try {
      const token = await apiService.getAuthToken();
      if (!token) throw new Error("Authentication required");
      const response = await authenticatedApi.post<any>(
        `/api/templates/${template.id}/preview`,
        {
          sampleData: {
            participant_first_name: "John",
            participant_id: "P123456",
            study_name: studyId,
            device_type: "Fitbit",
            last_sync_date: new Date().toLocaleDateString(),
            alert_type: template.alert_types[0] || "General",
            alert_date: new Date().toLocaleDateString(),
            researcher_name: "Dr. Smith",
            researcher_email: "dr.smith@example.com",
          },
        },
        token
      );

      if (response.success) {
        setPreviewData(response.preview);
        setShowPreviewModal(true);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to preview template",
        severity: "error",
      });
    }
  };

  const customTemplates = templates.filter((t) => t.is_custom);
  const defaultTemplates = templates.filter((t) => !t.is_custom);

  // Filter and search templates
  const filteredCustomTemplates = useMemo(() => {
    let filtered = customTemplates;

    // Search by name
    if (searchTerm) {
      filtered = filtered.filter((t) =>
        t.template_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by alert type
    if (selectedAlertTypeFilter !== "all") {
      filtered = filtered.filter((t) =>
        t.alert_types.includes(selectedAlertTypeFilter)
      );
    }

    return filtered;
  }, [customTemplates, searchTerm, selectedAlertTypeFilter]);

  const filteredDefaultTemplates = useMemo(() => {
    let filtered = defaultTemplates;

    // Search by name
    if (searchTerm) {
      filtered = filtered.filter((t) =>
        t.template_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by alert type
    if (selectedAlertTypeFilter !== "all") {
      filtered = filtered.filter((t) =>
        t.alert_types.includes(selectedAlertTypeFilter)
      );
    }

    return filtered;
  }, [defaultTemplates, searchTerm, selectedAlertTypeFilter]);

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Contact Message Templates
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenEditor()}
        >
          New Template
        </Button>
      </Box>

      {/* Available Variables Instructions */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1.5 }}>
          📋 Available Template Variables
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Use these variables in your templates. They will be automatically
          replaced with actual data when sent to participants:
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: 1,
          }}
        >
          {availableVariables.map((v) => (
            <Typography
              key={v}
              variant="caption"
              sx={{
                fontFamily: "monospace",
                bgcolor: "#ffffff",
                p: 1,
                borderRadius: 0.5,
                border: "1px solid #e0e0e0",
              }}
            >
              {v}
            </Typography>
          ))}
        </Box>
      </Alert>

      {/* Search and Filter */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          placeholder="Search by template name..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1, minWidth: "250px" }}
        />
        <FormControl size="small" sx={{ minWidth: "200px" }}>
          <InputLabel>Alert Type</InputLabel>
          <Select
            value={selectedAlertTypeFilter}
            label="Alert Type"
            onChange={(e) => setSelectedAlertTypeFilter(e.target.value)}
          >
            <MenuItem value="all">All Alert Types</MenuItem>
            {alertTypeOptions.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`Custom (${filteredCustomTemplates.length})`} id="tab-0" />
        <Tab
          label={`System Presets (${filteredDefaultTemplates.length})`}
          id="tab-1"
        />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : filteredCustomTemplates.length === 0 ? (
          <Alert severity="info">
            {searchTerm || selectedAlertTypeFilter !== "all"
              ? "No templates match your search criteria"
              : "No custom templates created yet. Create one to get started!"}
          </Alert>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {filteredCustomTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={() => handleOpenEditor(template)}
                onDelete={() => handleDeleteTemplate(template.id)}
                onPreview={() => handlePreview(template)}
                onMakeDefault={() =>
                  handleMakeDefault(template.id, template.alert_types[0])
                }
                onDuplicate={() => handleDuplicateTemplate(template)}
                readonly={false}
                alertTypeOptions={alertTypeOptions}
              />
            ))}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {filteredDefaultTemplates.length === 0 ? (
          <Alert severity="info">
            {searchTerm || selectedAlertTypeFilter !== "all"
              ? "No templates match your search criteria"
              : "No default templates available. Run seed script to populate."}
          </Alert>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {filteredDefaultTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={() => {}}
                onDelete={() => {}}
                onPreview={() => handlePreview(template)}
                onDuplicate={() => handleDuplicateTemplate(template)}
                readonly={true}
                alertTypeOptions={alertTypeOptions}
              />
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Editor Modal */}
      <TemplateEditorModal
        open={showEditorModal}
        template={editingTemplate}
        formData={formData}
        setFormData={setFormData}
        availableVariables={availableVariables}
        alertTypeOptions={alertTypeOptions}
        onSave={handleSaveTemplate}
        onClose={() => setShowEditorModal(false)}
      />

      {/* Preview Modal */}
      <PreviewModal
        open={showPreviewModal}
        previewData={previewData}
        onClose={() => setShowPreviewModal(false)}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

const TemplateCard: React.FC<{
  template: Template;
  onEdit: () => void;
  onDelete: () => void;
  onPreview: () => void;
  onDuplicate?: () => void;
  onMakeDefault?: () => void;
  readonly?: boolean;
  alertTypeOptions?: string[];
}> = ({
  template,
  onEdit,
  onDelete,
  onPreview,
  onDuplicate,
  onMakeDefault,
  readonly = false,
  alertTypeOptions = [],
}) => {
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ wordBreak: "break-word" }}>
              {template.template_name}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              {template.subject_line}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {!template.is_custom && (
              <Chip label="Default" size="small" color="primary" />
            )}
            {template.is_default && (
              <Chip
                label="★ Default for alert"
                size="small"
                color="success"
                icon={<StarIcon />}
              />
            )}
          </Box>
        </Box>

        {template.alert_types && template.alert_types.length > 0 && (
          <Box sx={{ my: 2 }}>
            {template.alert_types.map((type) => (
              <Chip
                key={type}
                label={type}
                size="small"
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
          </Box>
        )}
        {template.usage_count !== undefined && (
          <Typography variant="caption" color="textSecondary">
            Used {template.usage_count} times
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: "space-between", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Preview">
            <Button
              size="small"
              startIcon={<PreviewIcon />}
              onClick={onPreview}
            >
              Preview
            </Button>
          </Tooltip>
          {readonly && onDuplicate && (
            <Tooltip title="Duplicate to Custom">
              <Button
                size="small"
                startIcon={<FileCopyIcon />}
                onClick={onDuplicate}
              >
                Duplicate
              </Button>
            </Tooltip>
          )}
          {!readonly && (
            <>
              <Tooltip title="Edit">
                <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
                  Edit
                </Button>
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={onDelete}
                >
                  Delete
                </Button>
              </Tooltip>
              {onMakeDefault && (
                <Tooltip title="Set as default for this alert type">
                  <Button
                    size="small"
                    color={template.is_default ? "success" : "inherit"}
                    startIcon={
                      template.is_default ? <StarIcon /> : <StarOutlineIcon />
                    }
                    onClick={onMakeDefault}
                  >
                    {template.is_default ? "Default" : "Make Default"}
                  </Button>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </CardActions>
    </Card>
  );
};

const TemplateEditorModal: React.FC<{
  open: boolean;
  template: Template | null;
  formData: any;
  setFormData: (data: any) => void;
  availableVariables: string[];
  alertTypeOptions: string[];
  onSave: () => void;
  onClose: () => void;
}> = ({
  open,
  template,
  formData,
  setFormData,
  availableVariables,
  alertTypeOptions,
  onSave,
  onClose,
}) => {
  const insertVariable = (
    variable: string,
    field: "subjectLine" | "messageBody"
  ) => {
    const fieldValue = formData[field];
    const start =
      (document.getElementById(`${field}-input`) as HTMLTextAreaElement)
        ?.selectionStart || fieldValue.length;
    const newValue =
      fieldValue.slice(0, start) + variable + fieldValue.slice(start);
    setFormData({ ...formData, [field]: newValue });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {template ? "Edit Template" : "Create New Template"}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Template Name"
            value={formData.templateName}
            onChange={(e) =>
              setFormData({ ...formData, templateName: e.target.value })
            }
            fullWidth
            required
            placeholder="e.g., No Sync Reminder"
          />

          <TextField
            label="Email Subject"
            value={formData.subjectLine}
            onChange={(e) =>
              setFormData({ ...formData, subjectLine: e.target.value })
            }
            fullWidth
            required
            placeholder="e.g., [{{study_name}}] Device Sync Reminder"
            multiline
            rows={2}
            id="subjectLine-input"
          />

          {/* Alert Type - Required Single Selection */}
          <FormControl fullWidth required>
            <InputLabel>Alert Type</InputLabel>
            <Select
              value={formData.alertType}
              onChange={(e) =>
                setFormData({ ...formData, alertType: e.target.value })
              }
              label="Alert Type"
            >
              {alertTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography
            variant="subtitle2"
            sx={{ mt: 2, mb: 1, fontWeight: 600 }}
          >
            Message Body <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            value={formData.messageBody}
            onChange={(e) =>
              setFormData({ ...formData, messageBody: e.target.value })
            }
            fullWidth
            required
            multiline
            rows={12}
            placeholder="Dear {{participant_first_name}}, ..."
            id="messageBody-input"
            helperText="Use variables like {{participant_first_name}}, {{study_name}}, etc. in your message"
          />

          {/* Variables Reference */}
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Available Variables - Click to insert:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {availableVariables.map((variable) => (
                <Chip
                  key={variable}
                  label={variable}
                  onClick={() => insertVariable(variable, "messageBody")}
                  clickable
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={
            !formData.templateName ||
            !formData.subjectLine ||
            !formData.messageBody ||
            !formData.alertType
          }
        >
          Save Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PreviewModal: React.FC<{
  open: boolean;
  previewData: any;
  onClose: () => void;
}> = ({ open, previewData, onClose }) => {
  if (!previewData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Template Preview
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              Subject:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                p: 2,
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                fontFamily: "monospace",
                wordBreak: "break-word",
              }}
            >
              {previewData.subject}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              Message Body:
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {previewData.body}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateEditor;
